
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { puttingSituations } from "@/data/puttingSituations";
import { liftedApproaches } from "@/data/approaches/liftedApproaches";
import { longChips } from "@/data/approaches/longChips";
import { shortChips } from "@/data/approaches/shortChips"; 
import { wedging50m } from "@/data/approaches/wedging50m";
import { wedging30m } from "@/data/approaches/wedging30m";
import { shortBunker } from "@/data/approaches/shortBunker";
import { longBunker } from "@/data/approaches/longBunker";
import { midDistanceApproaches } from "@/data/approaches/midDistanceApproaches";
import { roughApproaches } from "@/data/approaches/roughApproaches";
import { AlertCircle } from "lucide-react";
import { calculateGlobalIndex } from "@/utils/indexCalculator";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Separator } from "@/components/ui/separator";
import { ResultsHeader } from "@/components/results/ResultsHeader";
import { PuttingResults } from "@/components/results/PuttingResults";
import { ApproachResults } from "@/components/results/ApproachResults";
import { TotalResults } from "@/components/results/TotalResults";
import { calculateApproachIndex } from "@/types/results";
import { TestHistory } from "@/components/results/TestHistory";
import NavigationMenu from "@/components/layout/NavigationMenu";
import { usePuttingResults } from "@/hooks/usePuttingResults";

const Results = () => {
  const { t } = useTranslation('results');
  const [totalPoints, setTotalPoints] = useState(0);
  const [globalIndex, setGlobalIndex] = useState(0);
  const [approachPoints, setApproachPoints] = useState(0);
  const [approachIndex, setApproachIndex] = useState(0);
  const [puttingPoints, setPuttingPoints] = useState(0);
  const [puttingIndex, setPuttingIndex] = useState(0);

  // Utilisation du hook spécifique pour les tests de Pelz
  const { puttingResults, isLoading, error } = usePuttingResults();

  useEffect(() => {
    if (puttingResults) {
      console.log('Results page - Total puttingResults:', puttingResults.length);
      console.log('Results page - situation_ids:', puttingResults.map(r => r.situation_id));
      
      const points = puttingResults.reduce((sum, result) => sum + result.points, 0);
      setTotalPoints(points);
      setGlobalIndex(calculateGlobalIndex(points));

      const approachResults = puttingResults.filter(r => r.situation_id >= 7 && r.situation_id <= 15);
      console.log('Results page - Approach results:', approachResults.length);
      console.log('Results page - Approach situation_ids:', approachResults.map(r => r.situation_id));
      
      const approachTotalPoints = approachResults.reduce((sum, result) => sum + result.points, 0);
      setApproachPoints(approachTotalPoints);
      setApproachIndex(calculateApproachIndex(approachTotalPoints));

      const puttingOnlyResults = puttingResults.filter(r => r.situation_id <= 6);
      console.log('Results page - Putting only results:', puttingOnlyResults.length);
      
      const puttingTotalPoints = puttingOnlyResults.reduce((sum, result) => sum + result.points, 0);
      setPuttingPoints(puttingTotalPoints);
      setPuttingIndex(calculateGlobalIndex(puttingTotalPoints));
    }
  }, [puttingResults]);

  if (isLoading) {
    return (
      <>
        <NavigationMenu />
        <div className="container mx-auto p-8">{t('page.loading')}</div>
      </>
    );
  }

  if (error) {
    return (
      <>
        <NavigationMenu />
        <div className="container mx-auto p-8">
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              {t('page.error')}
            </AlertDescription>
          </Alert>
        </div>
      </>
    );
  }

  if (!puttingResults || puttingResults.length === 0) {
    return (
      <>
        <NavigationMenu />
        <div className="container mx-auto p-8">
          <ResultsHeader />
          <Alert>
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              {t('page.noResults')}
            </AlertDescription>
          </Alert>
        </div>
      </>
    );
  }

  // Combine all situations
  const approachSituations = [
    liftedApproaches,
    longChips,
    shortChips,
    wedging50m,
    wedging30m,
    shortBunker,
    longBunker,
    midDistanceApproaches,
    roughApproaches
  ];
  const puttingSituationsOnly = puttingSituations;

  return (
    <>
      <NavigationMenu />
      <div className="container mx-auto p-8">
        <ResultsHeader />

        <div className="space-y-8">
          <PuttingResults
            puttingSituations={puttingSituationsOnly}
            results={puttingResults}
            totalPoints={puttingPoints}
            totalIndex={puttingIndex}
          />

          <TestHistory
            results={puttingResults}
            title={t('putting.history')}
            situationIds={puttingSituationsOnly.map(s => s.id)}
          />

          <Separator className="my-8" />

          <ApproachResults
            approachSituations={approachSituations}
            results={puttingResults}
            totalPoints={approachPoints}
            totalIndex={approachIndex}
          />

          <TestHistory
            results={puttingResults}
            title={t('approach.history')}
            situationIds={approachSituations.map(s => s.id)}
          />

          <Separator className="my-8" />

          <TotalResults
            totalPoints={totalPoints}
            globalIndex={globalIndex}
          />
        </div>
      </div>
    </>
  );
};

export default Results;
